import { AnimatePresence, motion } from 'motion/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Text } from '@/components/base';
import { NetworkStatus, useNetworkStatus } from '@/hooks/networkStatus/useNetworkStatus';
import { useNetworkStatusNotificationDeterminer } from '@/hooks/networkStatus/useNetworkStatusNotificationDeterminer';
import { toClassNames } from '@/utils';

interface NetworkStatusNotificationProps {
  className?: string;
}

const SHOW_ONLINE_NOTIFICATION_TIME = 3;
const SHOW_UNSTABLE_NOTIFICATION_TIME = 15;

const NetworkStatusNotification: React.FC<NetworkStatusNotificationProps> = ({ className = '' }) => {
  const { status } = useNetworkStatus();
  const { t } = useTranslation('network');

  const shouldSeeNetworkStatusNotification = useNetworkStatusNotificationDeterminer();

  const [previousStatus, setPreviousStatus] = useState<NetworkStatus>(status);
  const [showOnlineNotification, setShowOnlineNotification] = useState(false);
  const [showUnstableNotification, setShowUnstableNotification] = useState(false);

  // Detect status transitions during render and surface the notification immediately (avoids
  // setState-in-effect). The auto-hide timers run in the effect below.
  const wentOnline = previousStatus !== NetworkStatus.ONLINE && status === NetworkStatus.ONLINE;
  const wentUnstable = previousStatus !== NetworkStatus.UNSTABLE && status === NetworkStatus.UNSTABLE;
  if (wentOnline) {
    setPreviousStatus(status);
    setShowOnlineNotification(true);
    setShowUnstableNotification(false);
  } else if (wentUnstable) {
    setPreviousStatus(status);
    setShowUnstableNotification(true);
  } else if (previousStatus !== status) {
    setPreviousStatus(status);
    setShowUnstableNotification(false);
  }

  useEffect(() => {
    if (showOnlineNotification) {
      const timer = setTimeout(() => {
        setShowOnlineNotification(false);
      }, SHOW_ONLINE_NOTIFICATION_TIME * 1000);
      return () => clearTimeout(timer);
    }
  }, [showOnlineNotification]);

  useEffect(() => {
    if (showUnstableNotification) {
      const timer = setTimeout(() => {
        setShowUnstableNotification(false);
      }, SHOW_UNSTABLE_NOTIFICATION_TIME * 1000);
      return () => clearTimeout(timer);
    }
  }, [showUnstableNotification]);

  const notification = useMemo(() => {
    if (showOnlineNotification) {
      return {
        text: t('status.online.title'),
        subtext: t('status.online.subtitle'),
      };
    }

    if (status === NetworkStatus.OFFLINE) {
      return {
        text: t('status.offline.title'),
        subtext: t('status.offline.subtitle'),
      };
    }

    if (status === NetworkStatus.UNSTABLE && showUnstableNotification) {
      return {
        text: t('status.unstable.title'),
        subtext: t('status.unstable.subtitle'),
      };
    }

    return null;
  }, [status, t, showOnlineNotification, showUnstableNotification]);

  if (!shouldSeeNetworkStatusNotification) {
    return;
  }

  return (
    <AnimatePresence mode="wait">
      {notification && (
        <motion.div
          key={`${status}-${showOnlineNotification}-${showUnstableNotification}`}
          className={toClassNames(
            'fixed left-0 right-0 top-0 z-50 flex justify-center bg-revamp-gray-800/70 text-white shadow-lg backdrop-blur-sm',
            className
          )}
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          <div className="flex w-full max-w-[768px] px-6 py-4">
            <div className="flex items-center space-x-3">
              {status === NetworkStatus.ONLINE ? (
                <Image
                  src="/assets/images/network/online.svg"
                  alt="network-online"
                  className="aspect-square h-10 w-10"
                />
              ) : (
                <Image
                  src="/assets/images/network/unstable.svg"
                  alt="network-unstable"
                  className="aspect-square h-10 w-10"
                />
              )}
              <div className="text-left">
                <Text version="revamp-v1" className="font-semibold">
                  {notification.text}
                </Text>
                <Text version="revamp-v1" variant="xs">
                  {notification.subtext}
                </Text>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NetworkStatusNotification;
